import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Space,
  Card,
  Row,
  Col,
  Tag,
  Modal,
  Empty,
  Tabs,
  Timeline,
  Descriptions,
  Alert,
  Upload,
  Tooltip
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  TeamOutlined,
  IdcardOutlined,
  UploadOutlined,
  FileTextOutlined,
  DollarOutlined,
  PaperClipOutlined,
  CheckSquareOutlined,
  LinkOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import AttachmentUpload from './AttachmentUpload';
import EmployeeTasks from './EmployeeTasks';
import ImageViewer from './ImageViewer';
import EmployeeSalaryForm from './EmployeeSalaryForm';
import EmployeeUploadCard from './EmployeeUploadCard';
import PerformanceSalaryCalculator from './PerformanceSalaryCalculator';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

// 兜底修复历史 latin1 误读的中文文件名（后端已迁移，此处在展示层双保险）
const decodeFileName = (name) => {
  if (!name || typeof name !== 'string') return name;
  if (/[一-龥]/.test(name)) return name; // 已是正常中文
  try {
    // 等价于 Buffer.from(name,'latin1').toString('utf8')
    const bytes = Uint8Array.from(Array.from(name).map((c) => c.charCodeAt(0) & 0xff));
    const recovered = new TextDecoder('utf-8').decode(bytes);
    if (/[一-龥]/.test(recovered)) return recovered;
  } catch (e) {
    // 解码失败保持原值
  }
  return name;
};

// 判断附件是否为图片：mimetype 为准，扩展名兜底（入职任务附件 mimetype 不可靠）
const IMAGE_FILE_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const isImageAttachment = (attachment) => {
  if (!attachment) return false;
  if (attachment.mimetype && String(attachment.mimetype).startsWith('image/')) return true;
  const name = attachment.original_name || attachment.originalName || attachment.filename || attachment.url || '';
  return IMAGE_FILE_RE.test(String(name).split('?')[0]);
};

// 线索状态映射函数
const getLeadStatusText = (status) => {
  const statusTexts = {
    'new': '新线索',
    'contacted': '已联系',
    'qualified': '合格',
    'unqualified': '不合格',
    'converted': '已转化',
    'non_employee': '非员工',
    'pending': '待面试',
    'interviewed': '已面试',
    'offered': '已发offer',
    'hired': '已入职',
    'rejected': '已拒绝',
    'onboarding': '入职中',
    'active': '在职中',
    'inactive': '已离职',
    'no_show': '未到场',
    'cancelled': '已取消',
    'resigning': '离职中'
  };
  return statusTexts[status] || status || '-';
};

// 线索状态颜色映射函数
const getLeadStatusColor = (status) => {
  const statusColors = {
    'new': 'blue',
    'contacted': 'cyan',
    'qualified': 'green',
    'unqualified': 'red',
    'converted': 'purple',
    'non_employee': 'default',
    'pending': 'orange',
    'interviewed': 'blue',
    'offered': 'green',
    'hired': 'green',
    'rejected': 'red',
    'onboarding': 'purple',
    'active': 'green',
    'inactive': 'red',
    'no_show': 'red',
    'cancelled': 'default',
    'resigning': 'orange'
  };
  return statusColors[status] || 'default';
};

// 面试状态映射函数
const getInterviewStatusText = (status) => {
  const statusTexts = {
    'pending': '待面试',
    'completed': '已完成',
    'suitable': '合适',
    'not_suitable': '不合适',
    'cancelled': '已取消',
    'no_show': '未到场',
    'passed': '通过',
    'failed': '未通过'
  };
  return statusTexts[status] || status || '-';
};

// 面试状态颜色映射函数
const getInterviewStatusColor = (status) => {
  const statusColors = {
    'pending': 'orange',
    'completed': 'blue',
    'suitable': 'green',
    'not_suitable': 'red',
    'cancelled': 'default',
    'no_show': 'red',
    'passed': 'green',
    'failed': 'red'
  };
  return statusColors[status] || 'default';
};

// 格式化日期时间显示
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-';
  
  const date = new Date(dateTimeStr);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // 如果是今天，只显示时间
  if (diffDays === 0) {
    return date.toLocaleString('zh-CN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  }
  
  // 如果是一周内，显示星期几和时间
  if (diffDays < 7) {
    return date.toLocaleString('zh-CN', { 
      weekday: 'short',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  }
  
  // 其他情况显示完整日期
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

// 员工详情页样式（仅作用于 .employee-detail 根节点，避免污染其他页面）
const EMPLOYEE_DETAIL_CSS = `
.employee-detail { padding: 4px 4px 24px; }

/* ===== 头部信息卡 ===== */
.employee-detail .emp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}
.employee-detail .emp-header-main { display: flex; align-items: center; gap: 16px; min-width: 0; }
.employee-detail .emp-avatar {
  width: 56px; height: 56px; border-radius: 50%;
  background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
  color: #fff; font-size: 22px; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; box-shadow: 0 4px 10px rgba(22, 119, 255, 0.25);
}
.employee-detail .emp-header-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.employee-detail .emp-header-name { font-size: 19px; font-weight: 600; color: #1f2329; line-height: 28px; }
.employee-detail .emp-header-meta {
  margin-top: 6px; font-size: 13px; color: #86909c;
  display: flex; flex-wrap: wrap; align-items: center; row-gap: 4px;
}
.employee-detail .emp-header-meta .dot { margin: 0 10px; color: #d4d7dc; }
.employee-detail .emp-header-actions { flex-shrink: 0; }

/* ===== 卡片统一风格 ===== */
.employee-detail .ant-card {
  border-radius: 10px;
  border-color: #f0f0f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}
.employee-detail .emp-section-card .ant-card-head-title {
  position: relative;
  padding-left: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2329;
}
.employee-detail .emp-section-card .ant-card-head-title::before {
  content: '';
  position: absolute;
  left: 0; top: 50%;
  transform: translateY(-50%);
  width: 3px; height: 14px;
  border-radius: 2px;
  background: #1677ff;
}

/* ===== 只读态分组小标题 ===== */
.employee-detail .emp-view-group-title {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #4e5969 !important;
  margin: 12px 0 4px !important;
  padding-left: 8px;
  border-left: 3px solid #e5e6eb;
  line-height: 18px;
}

/* ===== 只读态：把 disabled 表单控件渲染为纯文本 ===== */
.employee-detail .emp-mode-view .ant-form-item { margin-bottom: 14px; }
.employee-detail .emp-mode-view .ant-form-item-label { padding-bottom: 4px; }
.employee-detail .emp-mode-view .ant-form-item-label > label {
  height: 22px; font-size: 13px; color: #86909c; font-weight: 400;
}
.employee-detail .emp-mode-view .ant-form-item-label > label.ant-form-item-required::before { display: none; }

/* Input / TextArea / InputNumber / DatePicker 去框 */
.employee-detail .emp-mode-view .ant-input[disabled],
.employee-detail .emp-mode-view .ant-input-disabled,
.employee-detail .emp-mode-view textarea.ant-input[disabled] {
  background-color: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  color: #1f2329;
  cursor: default;
  padding: 0;
  font-size: 14px;
}
.employee-detail .emp-mode-view textarea.ant-input[disabled] { padding: 0; resize: none; }
.employee-detail .emp-mode-view .ant-input-number-disabled {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  width: 100%;
}
.employee-detail .emp-mode-view .ant-input-number-disabled .ant-input-number-input {
  padding-left: 0; padding-right: 0; height: 24px;
  color: #1f2329; font-size: 14px; cursor: default;
}
.employee-detail .emp-mode-view .ant-input-number-disabled .ant-input-number-handler-wrap { display: none; }
.employee-detail .emp-mode-view .ant-picker-disabled {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  width: 100%;
  padding: 0;
  cursor: default;
}
.employee-detail .emp-mode-view .ant-picker-disabled .ant-picker-input > input {
  padding-left: 0; color: #1f2329; font-size: 14px; cursor: default;
}

/* Select 去框 */
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selector {
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  height: 24px !important;
}
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selection-wrap,
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selection-search,
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selection-overflow { align-items: center; }
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selection-item {
  color: #1f2329; font-size: 14px; line-height: 24px; padding-inline-end: 0;
}
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-arrow,
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-prefix,
.employee-detail .emp-mode-view .ant-picker-disabled .ant-picker-suffix,
.employee-detail .emp-mode-view .ant-picker-disabled .ant-picker-prefix,
.employee-detail .emp-mode-view .ant-input-prefix { display: none; }

/* 空值 placeholder 隐藏（只读态不显示"请输入"提示） */
.employee-detail .emp-mode-view .ant-input[disabled]::placeholder,
.employee-detail .emp-mode-view input.ant-input[disabled]::placeholder,
.employee-detail .emp-mode-view textarea.ant-input[disabled]::placeholder,
.employee-detail .emp-mode-view .ant-input-number-disabled input::placeholder,
.employee-detail .emp-mode-view .ant-picker-disabled input::placeholder,
.employee-detail .emp-mode-view .ant-select-disabled .ant-select-selection-placeholder {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}

/* ===== 证件与收款：只读态淡化空上传位 ===== */
.employee-detail .emp-mode-view .ant-upload-disabled.ant-upload-select {
  background: #fafbfc;
  border: 1px dashed #f0f0f0;
  cursor: default;
}
.employee-detail .emp-mode-view .ant-upload-disabled .ant-upload-btn { color: #c9cdd4; cursor: default; }
.employee-detail .emp-mode-view .ant-upload-disabled .anticon { color: #c9cdd4 !important; }
`;

const EmployeeDetail = ({
  employee,
  onSave,
  onStatusChange,
  departments = [],
  positions = []
}) => {
  const { user } = useAuth();
  const [basicEditMode, setBasicEditMode] = useState(false);
  const [workEditMode, setWorkEditMode] = useState(false);
  const [uploadEditMode, setUploadEditMode] = useState(false);
  const [careEditMode, setCareEditMode] = useState(false);
  const [form] = Form.useForm();
  const [regularModalVisible, setRegularModalVisible] = useState(false);
  const [regularDate, setRegularDate] = useState(null);
  const [resignModalVisible, setResignModalVisible] = useState(false);
  const [resignDate, setResignDate] = useState(null);
  const [resignReason, setResignReason] = useState('');
  const [resignAttachments, setResignAttachments] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [employeeAttachments, setEmployeeAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [lifecycleData, setLifecycleData] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [createLeadLoading, setCreateLeadLoading] = useState(false);
  // 图片查看器状态
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });
  // 薪资管理状态
  const [salaryData, setSalaryData] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryModalVisible, setSalaryModalVisible] = useState(false);
  const [salaryTemplates, setSalaryTemplates] = useState([]);
  // 业绩管理状态
  const [performanceModalVisible, setPerformanceModalVisible] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  // 职位联动状态
  const [filteredPositions, setFilteredPositions] = useState([]);
  // 合同管理状态
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractModalVisible, setContractModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm] = Form.useForm();
  // 合同附件上传状态
  const [contractUploadModalVisible, setContractUploadModalVisible] = useState(false);
  const [uploadingContractId, setUploadingContractId] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // 检查员工是否已离职
  const isEmployeeResigned = employee && (employee.status === 'resigned' || employee.status === 'terminated' || employee.status === 'resigning');

  const loadEmployeeAttachments = useCallback(async () => {
    if (!employee?.id) return;
    setAttachmentsLoading(true);
    try {
      const res = await api.get(`/upload/employee/${employee.id}`);
      setEmployeeAttachments(res.data?.data || []);
    } catch (e) {
      setEmployeeAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [employee?.id]);

  // 加载员工薪资数据
  const loadEmployeeSalary = useCallback(async () => {
    if (!employee?.id) return;
    setSalaryLoading(true);
    try {
      // 构建薪资模板查询参数
      let templateQuery = '';
      let positionId = employee.position_id;
      
      // 如果没有position_id但有position_name，先通过岗位名称查找岗位ID
      if (!positionId && employee.position_name) {
        try {
          const positionsRes = await fetch('/api/positions', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (positionsRes.ok) {
            const positionsResult = await positionsRes.json();
            const matchedPosition = positionsResult.data?.find(p => p.name === employee.position_name);
            if (matchedPosition) {
              positionId = matchedPosition.id;
              console.log('🔍 通过岗位名称找到岗位ID:', employee.position_name, '->', positionId);
            }
          }
        } catch (error) {
          console.warn('查找岗位ID失败:', error);
        }
      }
      
      if (positionId) {
        templateQuery = `position_id=${positionId}&is_active=true`;
      } else if (employee.position_name) {
        // 如果仍然没有position_id，通过岗位名称查询
        templateQuery = `position_name=${encodeURIComponent(employee.position_name)}&is_active=true`;
      }
      
      console.log('🔍 员工薪资模板查询参数:', {
        employee_id: employee.id,
        position_id: positionId,
        position_name: employee.position_name,
        templateQuery
      });

      const [currentRes, historyRes, templatesRes] = await Promise.all([
        fetch(`/api/salary/employees/${employee.id}/current-salary`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/salary/employees/${employee.id}/salary-changes?limit=10`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        templateQuery ? fetch(`/api/salary/position-templates?${templateQuery}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }) : Promise.resolve({ ok: false, json: () => Promise.resolve({ data: [] }) })
      ]);

      if (currentRes.ok) {
        const result = await currentRes.json();
        console.log('💰 薪资数据加载结果:', result);
        setSalaryData(result.data);
      } else {
        console.warn('⚠️ 薪资数据加载失败:', currentRes.status, currentRes.statusText);
      }

      if (historyRes.ok) {
        const result = await historyRes.json();
        console.log('📊 薪资变更历史加载结果:', result);
        console.log('📊 薪资变更历史数据:', result.data);
        setSalaryHistory(result.data || []);
      } else {
        console.warn('⚠️ 薪资变更历史加载失败:', historyRes.status, historyRes.statusText);
      }

      if (templatesRes.ok) {
        const result = await templatesRes.json();
        setSalaryTemplates(result.data || []);
        console.log('📋 找到薪资模板:', result.data?.length || 0, '个');
      } else {
        console.warn('⚠️ 薪资模板查询失败或员工未设置岗位');
        setSalaryTemplates([]);
      }
    } catch (error) {
      console.error('加载员工薪资数据失败:', error);
    } finally {
      setSalaryLoading(false);
    }
  }, [employee?.id, employee?.position_id, employee?.position_name]);

  // 加载员工生命周期数据
  const loadEmployeeLifecycle = useCallback(async () => {
    if (!employee?.id) return;
    setLifecycleLoading(true);
    try {
      const res = await api.get(`/employees/${employee.id}/lifecycle`);
      setLifecycleData(res.data?.data || null);
    } catch (e) {
      console.warn('获取生命周期数据失败:', e);
      setLifecycleData(null);
    } finally {
      setLifecycleLoading(false);
    }
  }, [employee?.id]);

  // 手动创建线索
  const handleCreateLead = async () => {
    if (!employee?.id) return;
    
    // 检查员工是否有联系方式
    const hasPhone = employee.phone && employee.phone.trim() !== '';
    const hasEmergencyPhone = employee.emergency_contact_phone && employee.emergency_contact_phone.trim() !== '';
    
    if (!hasPhone && !hasEmergencyPhone) {
      message.warning('该员工缺少有效的联系方式，请先完善手机号信息后再创建线索', 5);
      return;
    }
    
    setCreateLeadLoading(true);
    try {
      const response = await api.post(`/employees/${employee.id}/create-lead`);
      if (response.data.success) {
        message.success(response.data.message || '线索创建成功');
        // 重新加载生命周期数据
        await loadEmployeeLifecycle();
      } else {
        message.error(response.data.message || '创建线索失败');
      }
    } catch (error) {
      console.error('创建线索失败:', error);
      const errorMsg = error.response?.data?.message || '创建线索失败';
      
      // 特殊处理缺少联系方式的情况
      if (error.response?.data?.error === 'MISSING_PHONE') {
        message.warning('该员工缺少有效的联系方式，请先完善手机号信息后再创建线索', 5);
      } else {
        message.error(errorMsg);
      }
    } finally {
      setCreateLeadLoading(false);
    }
  };

  // 打开图片查看器
  const openImageViewer = (imageUrl, imageName, downloadUrl) => {
    setCurrentImage({
      url: imageUrl,
      name: imageName || '图片',
      downloadUrl: downloadUrl || imageUrl
    });
    setImageViewerVisible(true);
  };

  // 关闭图片查看器
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  // 加载员工合同列表
  const loadEmployeeContracts = useCallback(async () => {
    if (!employee?.id) return;
    setContractsLoading(true);
    try {
      const response = await api.get(`/contracts/employees/${employee.id}/contracts`);
      if (response.data.success) {
        setContracts(response.data.data || []);
      }
    } catch (error) {
      console.error('加载员工合同失败:', error);
    } finally {
      setContractsLoading(false);
    }
  }, [employee?.id]);

  // 处理合同附件上传
  const handleUploadAttachment = (contractId) => {
    setUploadingContractId(contractId);
    setUploadingFiles([]);
    setContractUploadModalVisible(true);
  };

  const handleFileChange = (info) => {
    setUploadingFiles(info.fileList);
  };

  const handleUploadSubmit = async () => {
    if (uploadingFiles.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }

    setUploading(true);
    try {
      for (const fileItem of uploadingFiles) {
        if (fileItem.originFileObj) {
          const formData = new FormData();
          formData.append('file', fileItem.originFileObj);
          formData.append('file_category', 'contract_scan');

          await api.post(`/contracts/contracts/${uploadingContractId}/attachments`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      }
      
      message.success('文件上传成功');
      setContractUploadModalVisible(false);
      setUploadingFiles([]);
      loadEmployeeContracts(); // 重新加载合同列表
    } catch (error) {
      console.error('上传文件失败:', error);
      message.error('文件上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (contractId, attachmentId) => {
    // 检查用户权限，只有管理员可以删除附件
    if (!user || (user.role !== 'super_admin' && user.role !== 'hr_manager')) {
      message.error('您没有权限删除附件，请联系管理员');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个附件吗？',
      onOk: async () => {
        try {
          await api.delete(`/contracts/contracts/attachments/${attachmentId}`);
          message.success('附件删除成功');
          loadEmployeeContracts();
        } catch (error) {
          message.error('删除附件失败');
        }
      }
    });
  };

  useEffect(() => {
    loadEmployeeAttachments();
    loadEmployeeLifecycle();
    loadEmployeeSalary();
    loadEmployeeContracts();
  }, [employee?.id, loadEmployeeAttachments, loadEmployeeLifecycle, loadEmployeeSalary, loadEmployeeContracts]);

  useEffect(() => {
    if (employee && form) {
      const initialValues = {
        ...employee,
        hire_date: employee.hire_date ? dayjs(employee.hire_date) : null,
        birth_date: employee.birth_date ? dayjs(employee.birth_date) : null,
        probation_end_date: employee.probation_end_date ? dayjs(employee.probation_end_date) : null,
        regular_date: employee.regular_date ? dayjs(employee.regular_date) : null,
        child1_birthday: employee.child1_birthday ? dayjs(employee.child1_birthday) : null,
        child2_birthday: employee.child2_birthday ? dayjs(employee.child2_birthday) : null,
        child3_birthday: employee.child3_birthday ? dayjs(employee.child3_birthday) : null,
        father_birthday: employee.father_birthday ? dayjs(employee.father_birthday) : null,
        mother_birthday: employee.mother_birthday ? dayjs(employee.mother_birthday) : null,
        spouse_father_birthday: employee.spouse_father_birthday ? dayjs(employee.spouse_father_birthday) : null,
        spouse_mother_birthday: employee.spouse_mother_birthday ? dayjs(employee.spouse_mother_birthday) : null
      };

      // 计算并设置在职状态
      const employmentStatus = getEmploymentStatus(employee.status);
      initialValues.employment_status = employmentStatus.text === '在职' ? 'employed' : 'resigned';

      // 尝试将部门/职位名称映射为对应的ID，便于选择器使用
      if (initialValues.department_id == null && employee.department_name && Array.isArray(departments)) {
        const matchedDept = departments.find(d => d && d.name === employee.department_name);
        if (matchedDept) initialValues.department_id = matchedDept.id;
      }
      if (initialValues.position_id == null && employee.position_name && Array.isArray(positions)) {
        const matchedPos = positions.find(p => p && p.name === employee.position_name);
        if (matchedPos) initialValues.position_id = matchedPos.id;
      }

      form.setFieldsValue(initialValues);
    }
  }, [employee, form, departments, positions]);

  // 基本信息编辑/保存/取消
  const handleBasicEdit = () => setBasicEditMode(true);
  const handleBasicSave = async () => {
    try {
      const values = await form.validateFields([
        'name', 'gender', 'birth_date', 'phone', 'id_card', 'address',
        'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relation'
      ]);
      const payload = {
        name: values.name,
        gender: values.gender,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
        phone: values.phone,
        id_card: values.id_card,
        address: values.address,
        emergency_contact_name: values.emergency_contact_name,
        emergency_contact_phone: values.emergency_contact_phone,
        emergency_contact_relation: values.emergency_contact_relation
      };
      await onSave(employee.id, payload);
      setBasicEditMode(false);
      message.success('基本信息已保存');
    } catch (error) {
      console.error('保存基本信息失败:', error);
      message.error('保存失败，请检查基本信息');
    }
  };
  const handleBasicCancel = () => {
    setBasicEditMode(false);
    if (employee && form) {
      const resetValues = {
        name: employee.name,
        gender: employee.gender || null,
        birth_date: employee.birth_date ? dayjs(employee.birth_date) : null,
        phone: employee.phone || null,
        id_card: employee.id_card || null,
        address: employee.address || null,
        emergency_contact_name: employee.emergency_contact_name || null,
        emergency_contact_phone: employee.emergency_contact_phone || null,
        emergency_contact_relation: employee.emergency_contact_relation || null
      };
      form.setFieldsValue(resetValues);
    }
  };

  // 证件与收款信息编辑/保存/取消
  const handleUploadEdit = () => setUploadEditMode(true);
  const handleUploadSave = async () => {
    try {
      const values = await form.validateFields([
        'id_card_front', 'id_card_back', 'bank_card_number', 'bank_name',
        'wechat_qr_code', 'alipay_qr_code'
      ]);
      const payload = {
        id_card_front: values.id_card_front,
        id_card_back: values.id_card_back,
        bank_card_number: values.bank_card_number,
        bank_name: values.bank_name,
        wechat_qr_code: values.wechat_qr_code,
        alipay_qr_code: values.alipay_qr_code
      };
      await onSave(employee.id, payload);
      setUploadEditMode(false);
      message.success('证件与收款信息已保存');
    } catch (error) {
      console.error('保存证件与收款信息失败:', error);
      message.error('保存失败，请检查证件与收款信息');
    }
  };
  const handleUploadCancel = () => {
    setUploadEditMode(false);
    if (employee && form) {
      const resetValues = {
        id_card_front: employee.id_card_front || null,
        id_card_back: employee.id_card_back || null,
        bank_card_number: employee.bank_card_number || null,
        bank_name: employee.bank_name || null,
        wechat_qr_code: employee.wechat_qr_code || null,
        alipay_qr_code: employee.alipay_qr_code || null
      };
      form.setFieldsValue(resetValues);
    }
  };

  // 员工关怀信息编辑/保存/取消
  const handleCareEdit = () => setCareEditMode(true);
  const handleCareSave = async () => {
    try {
      const values = await form.validateFields([
        'ethnicity', 'religion', 'habitual_birthday', 'native_place_province', 'native_place_city',
        'health_status', 'hobby', 'idol',
        'marital_status', 'child1_gender', 'child1_birthday', 'child2_gender', 'child2_birthday',
        'child3_gender', 'child3_birthday', 'self_monthly_income', 'spouse_monthly_income',
        'parents_city', 'father_birthday', 'father_work_status', 'father_health_status',
        'mother_birthday', 'mother_work_status', 'mother_health_status',
        'spouse_parents_city', 'spouse_father_birthday', 'spouse_father_work_status', 'spouse_father_health_status',
        'spouse_mother_birthday', 'spouse_mother_work_status', 'spouse_mother_health_status',
        'pre_industry', 'pre_last_position', 'pre_annual_income',
        'referrer', 'most_admired_person', 'best_friend',
        'previous_position', 'previous_position_level', 'previous_annual_income',
        'current_position_level', 'current_annual_income',
        'target_position', 'target_annual_income', 'learning_content', 'learning_reason',
        'goal_2026_1', 'goal_2026_2', 'goal_2026_3'
      ]);
      const dateFields = ['child1_birthday', 'child2_birthday', 'child3_birthday', 'father_birthday', 'mother_birthday', 'spouse_father_birthday', 'spouse_mother_birthday'];
      const payload = { ...values };
      dateFields.forEach(field => {
        payload[field] = values[field] ? values[field].format('YYYY-MM-DD') : null;
      });
      await onSave(employee.id, payload);
      setCareEditMode(false);
      message.success('员工关怀信息已保存');
    } catch (error) {
      console.error('保存员工关怀信息失败:', error);
      message.error('保存失败，请检查员工关怀信息');
    }
  };
  const handleCareCancel = () => {
    setCareEditMode(false);
    if (employee && form) {
      const dateFields = ['child1_birthday', 'child2_birthday', 'child3_birthday', 'father_birthday', 'mother_birthday', 'spouse_father_birthday', 'spouse_mother_birthday'];
      const resetValues = {};
      dateFields.forEach(field => {
        resetValues[field] = employee[field] ? dayjs(employee[field]) : null;
      });
      const textFields = [
        'ethnicity', 'religion', 'habitual_birthday', 'native_place_province', 'native_place_city',
        'health_status', 'hobby', 'idol',
        'marital_status', 'child1_gender', 'child2_gender', 'child3_gender',
        'self_monthly_income', 'spouse_monthly_income',
        'parents_city', 'father_work_status', 'father_health_status',
        'mother_work_status', 'mother_health_status',
        'spouse_parents_city', 'spouse_father_work_status', 'spouse_father_health_status',
        'spouse_mother_work_status', 'spouse_mother_health_status',
        'pre_industry', 'pre_last_position', 'pre_annual_income',
        'referrer', 'most_admired_person', 'best_friend',
        'previous_position', 'previous_position_level', 'previous_annual_income',
        'current_position_level', 'current_annual_income',
        'target_position', 'target_annual_income', 'learning_content', 'learning_reason',
        'goal_2026_1', 'goal_2026_2', 'goal_2026_3'
      ];
      textFields.forEach(field => {
        resetValues[field] = employee[field] || null;
      });
      form.setFieldsValue(resetValues);
    }
  };

  // 部门变化时，过滤对应的职位
  const handleDepartmentChange = async (departmentId) => {
    if (departmentId) {
      try {
        const response = await api.get('/positions', {
          params: { department_id: departmentId }
        });
        if (response.data.success) {
          setFilteredPositions(response.data.data || []);
        } else {
          setFilteredPositions([]);
        }
      } catch (error) {
        console.error('获取职位列表失败:', error);
        setFilteredPositions([]);
      }
    } else {
      setFilteredPositions([]);
    }
    // 清空职位选择
    form.setFieldsValue({ position_id: undefined });
  };

  // 部门变化时，过滤对应的职位（不清空当前职位选择）
  const handleDepartmentChangeWithoutClearing = async (departmentId) => {
    if (departmentId) {
      try {
        const response = await api.get('/positions', {
          params: { department_id: departmentId }
        });
        if (response.data.success) {
          setFilteredPositions(response.data.data || []);
        } else {
          setFilteredPositions([]);
        }
      } catch (error) {
        console.error('获取职位列表失败:', error);
        setFilteredPositions([]);
      }
    } else {
      setFilteredPositions([]);
    }
    // 注意：这里不清空职位选择，保留当前值
  };

  // 工作信息编辑/保存/取消
  const handleWorkEdit = () => {
    setWorkEditMode(true);
    // 进入编辑模式时，根据当前部门加载职位，但不清空当前职位选择
    const deptId = employee.department_id || departments.find(d => d.name === employee.department_name)?.id;
    if (deptId) {
      handleDepartmentChangeWithoutClearing(deptId);
    }
  };
  const handleWorkSave = async () => {
    try {
      const values = await form.validateFields(['department_id', 'position_id', 'hire_date', 'status', 'work_location']);
      const payload = {
        department_id: values.department_id,
        position_id: values.position_id,
        status: values.status,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
        work_location: values.work_location || null
      };
      await onSave(employee.id, payload);
      setWorkEditMode(false);
      message.success('工作信息已保存');
    } catch (error) {
      console.error('保存工作信息失败:', error);
      message.error('保存失败，请检查工作信息');
    }
  };
  const handleWorkCancel = () => {
    setWorkEditMode(false);
    if (employee && form) {
      const deptId = employee.department_id != null ? employee.department_id : (departments.find(d => d.name === employee.department_name)?.id);
      const posId = employee.position_id != null ? employee.position_id : (positions.find(p => p.name === employee.position_name)?.id);
      // 计算并设置在职状态
      const employmentStatus = getEmploymentStatus(employee.status);
      const resetValues = {
        department_id: deptId || null,
        position_id: posId || null,
        hire_date: employee.hire_date ? dayjs(employee.hire_date) : null,
        status: employee.status || null,
        employment_status: employmentStatus.text === '在职' ? 'employed' : 'resigned'
      };
      form.setFieldsValue(resetValues);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'probation': '试用期',
      'regular': '正式员工',
      'resigning': '离职中',
      'resigned': '已离职',
      'terminated': '已解除'
    };
    return statusMap[status] || status || '-';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'probation': 'orange',
      'regular': 'green',
      'resigning': 'orange',
      'resigned': 'red',
      'terminated': 'red'
    };
    return colorMap[status] || 'default';
  };

  // 获取在职状态
  const getEmploymentStatus = (status) => {
    switch (status) {
      case 'probation':
      case 'regular':
      case 'resigning':
        return { color: 'green', text: '在职' };
      case 'resigned':
      case 'terminated':
        return { color: 'red', text: '离职' };
      default:
        return { color: 'default', text: '未知' };
    }
  };

  // 薪资处理函数
  const handleSalarySubmit = async (data) => {
    try {
      const response = await fetch(`/api/salary/employees/${employee.id}/salaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        message.success('员工薪资设置成功');
        loadEmployeeSalary(); // 重新加载薪资数据
        return true;
      } else {
        const error = await response.json();
        message.error(error.message || '薪资设置失败');
        return false;
      }
    } catch (error) {
      console.error('设置员工薪资失败:', error);
      message.error('薪资设置失败');
      return false;
    }
  };

  if (!employee) return null;

  // 基本信息Tab内容
  const renderBasicInfoTab = () => (
      <Form form={form} layout="vertical">
        <Card
          title="基本信息"
          size="small"
          className={`emp-section-card ${basicEditMode ? 'emp-mode-edit' : 'emp-mode-view'}`}
          style={{ marginBottom: 16 }}
          extra={
            !basicEditMode ? (
              <Button 
                icon={<EditOutlined />} 
                onClick={handleBasicEdit}
                disabled={isEmployeeResigned}
                title={isEmployeeResigned ? '离职员工无法编辑基本信息' : ''}
              >
                编辑
              </Button>
            ) : (
              <Space>
                <Button icon={<SaveOutlined />} type="primary" onClick={handleBasicSave}>保存</Button>
                <Button icon={<CloseOutlined />} onClick={handleBasicCancel}>取消</Button>
              </Space>
            )
          }
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="工号" name="employee_no">
                <Input disabled={true} prefix={<IdcardOutlined />} style={{ backgroundColor: '#f5f5f5' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="姓名" name="name">
                <Input disabled={!basicEditMode} prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="性别" name="gender">
                <Select disabled={!basicEditMode}>
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="出生日期" name="birth_date">
                <DatePicker disabled={!basicEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item 
                label="手机号" 
                name="phone"
                rules={[
                  {
                    pattern: /^1[3-9]\d{9}$/,
                    message: '请输入正确的手机号码格式'
                  }
                ]}
              >
                <Input disabled={!basicEditMode} prefix={<PhoneOutlined />} placeholder="请输入11位手机号" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="身份证号"
                name="id_card"
                rules={[
                  {
                    pattern: /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/,
                    message: '请输入正确的18位身份证号码'
                  }
                ]}
              >
                <Input disabled={!basicEditMode} prefix={<IdcardOutlined />} placeholder="请输入18位身份证号" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="地址" name="address">
                <Input disabled={!basicEditMode} />
              </Form.Item>
            </Col>
          </Row>


          {/* 紧急联系人 */}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item 
                label="紧急联系人姓名" 
                name="emergency_contact_name"
              >
                <Input disabled={!basicEditMode} placeholder="请输入紧急联系人姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="紧急联系人电话" 
                name="emergency_contact_phone"
                rules={[
                  {
                    pattern: /^1[3-9]\d{9}$/,
                    message: '请输入正确的手机号码格式'
                  }
                ]}
              >
                <Input disabled={!basicEditMode} placeholder="请输入紧急联系人电话" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="与本人关系" 
                name="emergency_contact_relation"
              >
                <Select disabled={!basicEditMode} placeholder="请选择关系">
                  <Option value="配偶">配偶</Option>
                  <Option value="父母">父母</Option>
                  <Option value="子女">子女</Option>
                  <Option value="兄弟姐妹">兄弟姐妹</Option>
                  <Option value="朋友">朋友</Option>
                  <Option value="同事">同事</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              {/* 空位置，保持布局一致 */}
            </Col>
          </Row>
        </Card>

        <Card
          title="工作信息"
          size="small"
          className={`emp-section-card ${workEditMode ? 'emp-mode-edit' : 'emp-mode-view'}`}
          style={{ marginBottom: 16 }}
          extra={
            !workEditMode ? (
              <Button 
                icon={<EditOutlined />} 
                onClick={handleWorkEdit}
                disabled={isEmployeeResigned && user?.role !== 'super_admin'}
                title={isEmployeeResigned && user?.role !== 'super_admin' ? '离职员工无法编辑工作信息' : (isEmployeeResigned ? '超级管理员可以编辑离职员工信息' : '')}
              >
                编辑
              </Button>
            ) : (
              <Space>
                <Button icon={<SaveOutlined />} type="primary" onClick={handleWorkSave}>保存</Button>
                <Button icon={<CloseOutlined />} onClick={handleWorkCancel}>取消</Button>
              </Space>
            )
          }
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="部门" name="department_id">
                <Select 
                  disabled={!workEditMode} 
                  prefix={<TeamOutlined />}
                  onChange={handleDepartmentChange}
                  placeholder="请选择部门"
                >
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="职位" name="position_id">
                <Select 
                  disabled={!workEditMode}
                  placeholder={filteredPositions.length > 0 ? "请选择职位" : "请先选择部门"}
                >
                  {(workEditMode && filteredPositions.length > 0 ? filteredPositions : positions).map(pos => (
                    <Option key={pos.id} value={pos.id}>{pos.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="入职日期" name="hire_date">
                <DatePicker disabled={!workEditMode} style={{ width: '100%'}} prefix={<CalendarOutlined />} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="进度" name="status">
                <Select disabled={!workEditMode}>
                  <Option value="probation">试用期</Option>
                  <Option value="regular">正式员工</Option>
                  <Option value="resigning">离职中</Option>
                  <Option value="resigned">已离职</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="在职状态" name="employment_status">
                <Select disabled>
                  <Option value="employed">在职</Option>
                  <Option value="resigned">离职</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="工作地点" name="work_location">
                <Select
                  disabled={!workEditMode}
                  placeholder="请选择工作地点"
                  allowClear
                  options={[
                    { label: '洛阳', value: '洛阳' },
                    { label: '汝州', value: '汝州' }
                  ]}
                />
              </Form.Item>
            </Col>
            {/* 只有管理员可以编辑试用期相关时间 */}
            {(user?.role === 'super_admin' || user?.role === 'hr_manager') && (
              <>
                <Col span={6}>
                  <Form.Item label="试用期结束" name="probation_end_date">
                    <DatePicker disabled={!workEditMode} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="转正日期" name="regular_date">
                    <DatePicker disabled={!workEditMode} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Card>

        {/* 证件与收款信息卡片 */}
        <Card
          title="证件与收款信息"
          size="small"
          className={`emp-section-card ${uploadEditMode ? 'emp-mode-edit' : 'emp-mode-view'}`}
          style={{ marginBottom: 16 }}
          extra={
            !uploadEditMode ? (
              <Button 
                icon={<EditOutlined />} 
                onClick={handleUploadEdit}
                disabled={isEmployeeResigned}
                title={isEmployeeResigned ? '离职员工无法编辑证件与收款信息' : ''}
              >
                编辑
              </Button>
            ) : (
              <Space>
                <Button icon={<SaveOutlined />} type="primary" onClick={handleUploadSave}>保存</Button>
                <Button icon={<CloseOutlined />} onClick={handleUploadCancel}>取消</Button>
              </Space>
            )
          }
        >
          <EmployeeUploadCard form={form} basicEditMode={uploadEditMode} employeeId={employee?.id} />
        </Card>

        {/* 员工关怀信息卡片 */}
        <Card
          title="员工关怀信息"
          size="small"
          className={`emp-section-card ${careEditMode ? 'emp-mode-edit' : 'emp-mode-view'}`}
          style={{ marginBottom: 16 }}
          extra={
            !careEditMode ? (
              <Button
                icon={<EditOutlined />}
                onClick={handleCareEdit}
                disabled={isEmployeeResigned}
                title={isEmployeeResigned ? '离职员工无法编辑员工关怀信息' : ''}
              >
                编辑
              </Button>
            ) : (
              <Space>
                <Button icon={<SaveOutlined />} type="primary" onClick={handleCareSave}>保存</Button>
                <Button icon={<CloseOutlined />} onClick={handleCareCancel}>取消</Button>
              </Space>
            )
          }
        >
          {/* 个人情况 */}
          <div className="emp-view-group-title" style={{ marginBottom: 8, fontWeight: 500, color: '#333' }}>个人情况</div>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="民族" name="ethnicity">
                <Input disabled={!careEditMode} placeholder="请输入民族" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="宗教" name="religion">
                <Input disabled={!careEditMode} placeholder="请输入宗教信仰" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="习惯过的生日" name="habitual_birthday">
                <Select disabled={!careEditMode} placeholder="请选择" allowClear>
                  <Option value="农历">农历</Option>
                  <Option value="阳历">阳历</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="健康情况" name="health_status">
                <Input disabled={!careEditMode} placeholder="请输入健康情况" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="籍贯（省）" name="native_place_province">
                <Input disabled={!careEditMode} placeholder="请输入省份" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="籍贯（市）" name="native_place_city">
                <Input disabled={!careEditMode} placeholder="请输入城市" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="兴趣项目" name="hobby">
                <Input disabled={!careEditMode} placeholder="请输入兴趣爱好" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="偶像" name="idol">
                <Input disabled={!careEditMode} placeholder="请输入偶像" />
              </Form.Item>
            </Col>
          </Row>

          {/* 家庭情况 */}
          <div className="emp-view-group-title" style={{ margin: '16px 0 8px', fontWeight: 500, color: '#333' }}>家庭情况</div>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="婚姻状况" name="marital_status">
                <Select disabled={!careEditMode} placeholder="请选择" allowClear>
                  <Option value="未婚">未婚</Option>
                  <Option value="已婚">已婚</Option>
                  <Option value="离异">离异</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="自己月收入（元）" name="self_monthly_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入月收入" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶月收入（元）" name="spouse_monthly_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入月收入" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="孩子1性别" name="child1_gender">
                <Select disabled={!careEditMode} placeholder="请选择" allowClear>
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="孩子1生日" name="child1_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="孩子2性别" name="child2_gender">
                <Select disabled={!careEditMode} placeholder="请选择" allowClear>
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="孩子2生日" name="child2_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="孩子3性别" name="child3_gender">
                <Select disabled={!careEditMode} placeholder="请选择" allowClear>
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="孩子3生日" name="child3_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="父母所居城市" name="parents_city">
                <Input disabled={!careEditMode} placeholder="请输入城市" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="父亲出生年月日" name="father_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="父亲工作情况" name="father_work_status">
                <Input disabled={!careEditMode} placeholder="请输入工作情况" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="父亲身体状况" name="father_health_status">
                <Input disabled={!careEditMode} placeholder="请输入身体状况" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="母亲出生年月日" name="mother_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="母亲工作情况" name="mother_work_status">
                <Input disabled={!careEditMode} placeholder="请输入工作情况" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="母亲身体状况" name="mother_health_status">
                <Input disabled={!careEditMode} placeholder="请输入身体状况" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="配偶父母所居城市" name="spouse_parents_city">
                <Input disabled={!careEditMode} placeholder="请输入城市" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶父亲出生年月日" name="spouse_father_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶父亲工作情况" name="spouse_father_work_status">
                <Input disabled={!careEditMode} placeholder="请输入工作情况" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶父亲身体状况" name="spouse_father_health_status">
                <Input disabled={!careEditMode} placeholder="请输入身体状况" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="配偶母亲出生年月日" name="spouse_mother_birthday">
                <DatePicker disabled={!careEditMode} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶母亲工作情况" name="spouse_mother_work_status">
                <Input disabled={!careEditMode} placeholder="请输入工作情况" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="配偶母亲身体状况" name="spouse_mother_health_status">
                <Input disabled={!careEditMode} placeholder="请输入身体状况" />
              </Form.Item>
            </Col>
          </Row>

          {/* 入职前工作情况 */}
          <div className="emp-view-group-title" style={{ margin: '16px 0 8px', fontWeight: 500, color: '#333' }}>入职前工作情况</div>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="所属行业" name="pre_industry">
                <Input disabled={!careEditMode} placeholder="请输入行业" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="最后职位" name="pre_last_position">
                <Input disabled={!careEditMode} placeholder="请输入职位" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="年收入（万）" name="pre_annual_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入年收入" />
              </Form.Item>
            </Col>
          </Row>

          {/* 入职后工作情况 */}
          <div className="emp-view-group-title" style={{ margin: '16px 0 8px', fontWeight: 500, color: '#333' }}>入职后工作情况</div>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="介绍人" name="referrer">
                <Input disabled={!careEditMode} placeholder="请输入介绍人" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="最佩服的人" name="most_admired_person">
                <Input disabled={!careEditMode} placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="私交最好的人" name="best_friend">
                <Input disabled={!careEditMode} placeholder="请输入姓名" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="上一任岗位" name="previous_position">
                <Input disabled={!careEditMode} placeholder="请输入岗位" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="上一任职位/级" name="previous_position_level">
                <Input disabled={!careEditMode} placeholder="请输入职位/级" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="上一任年收入（万）" name="previous_annual_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入年收入" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="现任职位/级" name="current_position_level">
                <Input disabled={!careEditMode} placeholder="请输入职位/级" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="现任年收入（万）" name="current_annual_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入年收入" />
              </Form.Item>
            </Col>
          </Row>

          {/* 职业发展与成长计划 */}
          <div className="emp-view-group-title" style={{ margin: '16px 0 8px', fontWeight: 500, color: '#333' }}>职业发展与成长计划</div>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="目标职位/级" name="target_position">
                <Input disabled={!careEditMode} placeholder="请输入目标职位/级" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="目标年收入（万）" name="target_annual_income">
                <InputNumber disabled={!careEditMode} style={{ width: '100%' }} min={0} placeholder="请输入目标年收入" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="学习成长内容" name="learning_content">
                <Input disabled={!careEditMode} placeholder="请输入学习内容" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="学习成长原因" name="learning_reason">
                <Input disabled={!careEditMode} placeholder="请输入学习原因" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="2026年核心目标1" name="goal_2026_1">
                <Input.TextArea disabled={!careEditMode} rows={2} placeholder="请输入目标1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="2026年核心目标2" name="goal_2026_2">
                <Input.TextArea disabled={!careEditMode} rows={2} placeholder="请输入目标2" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="2026年核心目标3" name="goal_2026_3">
                <Input.TextArea disabled={!careEditMode} rows={2} placeholder="请输入目标3" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
    </Form>
  );

  // 附件Tab内容
  const renderAttachmentsTab = () => (
    <div>
      <Card 
        title="附件管理" 
        size="small" 
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModalVisible(true)}>
            上传附件
          </Button>
        }
      >
        {attachmentsLoading ? (
          <div style={{ textAlign: 'center', padding: 16 }}>加载中...</div>
        ) : employeeAttachments.length === 0 ? (
          <Empty description="暂无附件" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
            {employeeAttachments.map((f) => {
              const img = isImageAttachment({ mimetype: f.mimetype, originalName: f.originalName, url: f.url });
              const fileName = decodeFileName(f.originalName || '附件');
              return (
                <div key={f.id} style={{ textAlign: 'center' }}>
                  {img ? (
                    <img
                      src={f.url}
                      alt={fileName}
                      style={{
                        width: 110,
                        height: 110,
                        objectFit: 'cover',
                        borderRadius: 6,
                        border: '1px solid #eee',
                        cursor: 'pointer'
                      }}
                      onClick={() => openImageViewer(f.url, fileName, f.url)}
                    />
                  ) : (
                    <div style={{ width: 110, height: 110, border: '1px solid #eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                      📎
                    </div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fileName}>{fileName}</div>
                  {img ? (
                    <span
                      style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => openImageViewer(f.url, fileName, f.url)}
                    >
                      预览
                    </span>
                  ) : (
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>下载</a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      
    </div>
  );

  // 任务Tab内容
  const renderTasksTab = () => (
        <EmployeeTasks employee={employee} />
  );

  // 薪资管理Tab内容
  const renderSalaryTab = () => {
    // 检查员工是否设置了岗位信息
    const hasPositionInfo = employee?.position_id || employee?.position_name;
    // 检查员工是否已离职
    const isEmployeeResigned = employee && (employee.status === 'resigned' || employee.status === 'terminated' || employee.status === 'resigning');
    
    return (
      <div>
        {/* 离职员工提示 */}
        {isEmployeeResigned && (
          <Alert
            message="该员工已离职"
            description={user?.role === 'super_admin' ? "作为超级管理员，您可以编辑离职员工的信息和状态。" : "离职员工的基本信息和工作信息无法编辑，如需修改请联系系统管理员。"}
            type={user?.role === 'super_admin' ? 'info' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {salaryLoading ? (
          <Card loading={true} title="加载薪资数据..." />
        ) : (
          <div>
            {/* 离职员工提示 */}
            {isEmployeeResigned && (
              <Alert
                message="该员工已离职，薪资信息已锁定"
                description="离职员工的薪资信息无法编辑，如需修改请联系系统管理员。"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 岗位信息提示 */}
            {!hasPositionInfo && !isEmployeeResigned && (
              <Alert
                message="员工未设置岗位信息"
                description={
                  <div>
                    <p>无法加载岗位薪资模板，请在员工基本信息中设置岗位和部门。</p>
                    <p>当前员工信息：</p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>岗位ID: {employee?.position_id || '未设置'}</li>
                      <li>岗位名称: {employee?.position_name || '未设置'}</li>
                      <li>部门ID: {employee?.department_id || '未设置'}</li>
                      <li>部门名称: {employee?.department_name || '未设置'}</li>
                    </ul>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 当前薪资概览 */}
            <Card 
              title="当前薪资配置" 
              size="small" 
              style={{ marginBottom: 16 }}
              extra={
                <Space>
                  <Button 
                    type="primary" 
                    icon={<DollarOutlined />}
                    onClick={() => setSalaryModalVisible(true)}
                    disabled={!hasPositionInfo || isEmployeeResigned}
                    title={
                      !hasPositionInfo ? '请先设置员工岗位信息' : 
                      isEmployeeResigned ? '离职员工无法编辑薪资' : ''
                    }
                  >
                    {salaryData ? '调整薪资' : '设置薪资'}
                  </Button>
                </Space>
              }
            >
            {salaryData ? (
              <div>
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="总薪资">
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                      ¥{salaryData.total_salary?.toLocaleString()}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="生效日期">{salaryData.effective_date}</Descriptions.Item>
                  <Descriptions.Item label="薪资模板">{salaryData.template_name || '自定义配置'}</Descriptions.Item>
                </Descriptions>
                
                {/* 薪资项目明细 */}
                <div style={{ marginTop: '16px' }}>
                  <h4>薪资项目明细</h4>
                  <Row gutter={[16, 8]}>
                    {salaryData.items?.map((item, index) => (
                      <Col span={6} key={index}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '12px', color: '#666' }}>{item.salary_item_name}</div>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: item.salary_item_type === 'deduction' ? '#f5222d' : '#52c41a'
                          }}>
                            {item.salary_item_type === 'deduction' ? '-' : '+'}
                            {item.calculation_type === 'percentage' ? 
                              `${parseFloat(item.percentage) || parseFloat(item.value) || 0}%` : 
                              `¥${item.calculated_amount?.toLocaleString() || 0}`
                            }
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            ) : (
              <Empty 
                description="暂未配置薪资"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          {/* 业绩信息 */}
          <Card title="业绩信息" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>部门业绩</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                    ¥{(Math.floor(Math.random() * 1000000) + 500000).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    {(Math.floor(Math.random() * 50) + 80)}% 完成
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>门店业绩</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                    ¥{(Math.floor(Math.random() * 500000) + 200000).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    {(Math.floor(Math.random() * 50) + 70)}% 完成
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>业绩奖金</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                    ¥{(Math.floor(Math.random() * 5000) + 1000).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    基于业绩完成比例
                  </div>
                </Card>
              </Col>
            </Row>
            
            {/* 业绩薪资计算器按钮 */}
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Button 
                type="primary" 
                icon={<DollarOutlined />}
                onClick={() => setPerformanceModalVisible(true)}
                disabled={isEmployeeResigned}
              >
                业绩薪资计算
              </Button>
            </div>
          </Card>

          {/* 薪资变更历史 */}
          <Card title="薪资变更历史" size="small">
            {salaryHistory.length > 0 ? (
              <div>
                {salaryHistory.map((change, index) => (
                  <Card 
                    key={index}
                    size="small" 
                    style={{ 
                      marginBottom: '12px',
                      borderLeft: `4px solid ${
                        change.approval_status === 'approved' ? '#52c41a' : 
                        change.approval_status === 'rejected' ? '#f5222d' : '#1890ff'
                      }`
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Row gutter={[16, 0]} align="middle">
                      <Col span={4}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '14px',
                          color: '#262626',
                          marginBottom: '4px'
                        }}>
                          {change.change_summary || '薪资变更'}
                        </div>
                        {change.approval_status && (
                          <Tag 
                            color={change.approval_status === 'approved' ? 'green' : 
                                   change.approval_status === 'rejected' ? 'red' : 'blue'}
                            size="small"
                          >
                            {change.approval_status === 'approved' ? '已审批' : 
                             change.approval_status === 'rejected' ? '已拒绝' : '待审批'}
                          </Tag>
                        )}
                      </Col>
                      
                      <Col span={6}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div style={{ marginBottom: '2px' }}>
                            <strong>变更原因：</strong>
                          </div>
                          <div style={{ wordBreak: 'break-word' }}>
                            {change.change_reason || '-'}
                          </div>
                        </div>
                      </Col>
                      
                      <Col span={4}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div style={{ marginBottom: '2px' }}>
                            <strong>生效日期：</strong>
                          </div>
                          <div>{change.effective_date || '-'}</div>
                        </div>
                      </Col>
                      
                      <Col span={4}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div style={{ marginBottom: '2px' }}>
                            <strong>操作人：</strong>
                          </div>
                          <div>{change.created_by_name || '系统'}</div>
                        </div>
                      </Col>
                      
                      <Col span={6}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div style={{ marginBottom: '2px' }}>
                            <strong>操作时间：</strong>
                          </div>
                          <div>{formatDateTime(change.created_at)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            ) : (
              <Empty 
                description="暂无薪资变更记录"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </div>
      )}
    </div>
    );
  };

  // 生命周期Tab内容（以线索ID为核心的完整时间线）
  const renderLifecycleTab = () => (
    <div>
      {lifecycleLoading ? (
        <Card loading={true} title="加载生命周期数据..." />
      ) : lifecycleData ? (
        <div>
          {/* 线索概览 */}
          {lifecycleData.lead ? (
            <Card size="small" style={{ marginBottom: 16 }} title={`📋 线索概览 (ID: ${lifecycleData.leadId})`}>
              <Descriptions column={4} size="small">
                <Descriptions.Item label="线索来源">{lifecycleData.lead.source_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{lifecycleData.lead.created_at || '-'}</Descriptions.Item>
                <Descriptions.Item label="当前状态">
                  <Tag color={getLeadStatusColor(lifecycleData.lead.status)}>
                    {getLeadStatusText(lifecycleData.lead.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="评级">{lifecycleData.lead.rating_name || lifecycleData.lead.rating || '-'}</Descriptions.Item>
                <Descriptions.Item label="创建人">{lifecycleData.lead.created_by_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="联系方式">{lifecycleData.lead.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="邮箱">{lifecycleData.lead.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="期望职位">{lifecycleData.lead.expected_position || '-'}</Descriptions.Item>
              </Descriptions>
              {lifecycleData.lead.notes && (
                <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                  <strong>线索备注：</strong>{lifecycleData.lead.notes}
                </div>
              )}
            </Card>
          ) : (
            <Alert 
              message="该员工没有关联的线索记录" 
              description="可能是直接入职或数据不完整，建议检查数据关联关系" 
              type="warning" 
              style={{ marginBottom: 16 }}
              action={
                <Tooltip 
                  title={
                    (!employee.phone || employee.phone.trim() === '') && 
                    (!employee.emergency_contact_phone || employee.emergency_contact_phone.trim() === '') 
                      ? '请先完善员工手机号信息后再创建线索' 
                      : ''
                  }
                >
                  <Button 
                    size="small" 
                    type="primary" 
                    icon={<LinkOutlined />}
                    loading={createLeadLoading}
                    onClick={handleCreateLead}
                    disabled={
                      (!employee.phone || employee.phone.trim() === '') && 
                      (!employee.emergency_contact_phone || employee.emergency_contact_phone.trim() === '')
                    }
                  >
                    创建线索
                  </Button>
                </Tooltip>
              }
            />
          )}

          {/* 完整时间线 */}
          <Card title="🕐 完整生命周期时间线" size="small">
            <Timeline>
              {/* 线索创建 */}
              {lifecycleData.lead && (
                <Timeline.Item color="blue">
                  <div><strong>线索创建</strong> - {formatDateTime(lifecycleData.lead.created_at)}</div>
                  <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
                    <Space size="middle" wrap>
                      <span>🎯 来源：{lifecycleData.lead.source_name || '-'}</span>
                      <span>👤 创建人：{lifecycleData.lead.created_by_name || '-'}</span>
                    </Space>
                  </div>
                </Timeline.Item>
              )}

              {/* 面试记录 */}
              {lifecycleData.interviews && lifecycleData.interviews.map((interview, index) => (
                <Timeline.Item key={interview.id} color={getInterviewStatusColor(interview.status)}>
                  <div>
                    <strong>面试 {index + 1}</strong> - {formatDateTime(interview.interview_date || interview.created_at)}
                  </div>
                  <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
                    <Space size="middle" wrap>
                      <span>👤 面试官：{interview.interviewer || '-'}</span>
                      <span>💼 职位：{interview.position || '-'}</span>
                      <span>
                        📋 状态：
                        <Tag 
                          color={getInterviewStatusColor(interview.status)} 
                          size="small" 
                          style={{ marginLeft: 4 }}
                        >
                          {getInterviewStatusText(interview.status)}
                        </Tag>
                      </span>
                    </Space>
                  </div>
                  {interview.notes && (
                    <div style={{ 
                      color: '#999', 
                      fontSize: '12px', 
                      marginTop: 6,
                      padding: '4px 8px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                      borderLeft: '3px solid #d9d9d9'
                    }}>
                      💬 备注：{interview.notes}
                    </div>
                  )}
                </Timeline.Item>
              ))}

              {/* 入职流程 */}
              {lifecycleData.onboarding && (
                <Timeline.Item color="purple">
                  <div><strong>入职流程创建</strong> - {formatDateTime(lifecycleData.onboarding.created_at)}</div>
                  <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
                    <Space size="middle" wrap>
                      <span>📅 入职日期：{lifecycleData.onboarding.start_date || '-'}</span>
                      <span>👤 创建人：{lifecycleData.onboarding.created_by_name || '-'}</span>
                    </Space>
                  </div>
                  
                  {/* 入职任务详情 */}
                  {lifecycleData.onboarding.onboarding_tasks && (() => {
                    try {
                      const tasks = JSON.parse(lifecycleData.onboarding.onboarding_tasks);
                      if (Array.isArray(tasks) && tasks.length > 0) {
                        return (
                          <div style={{ 
                            marginTop: 12, 
                            padding: '8px 12px', 
                            background: '#f8f9fa', 
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ 
                              fontSize: '13px', 
                              fontWeight: 'bold', 
                              color: '#495057', 
                              marginBottom: 8,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              📋 入职任务 ({tasks.length}个)
                            </div>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {tasks.map((task, index) => (
                                <div key={index} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '6px 8px',
                                  marginBottom: '4px',
                                  background: task.completed_at ? '#d4edda' : '#fff3cd',
                                  borderRadius: '4px',
                                  border: `1px solid ${task.completed_at ? '#c3e6cb' : '#ffeaa7'}`
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      fontWeight: 'bold',
                                      color: task.completed_at ? '#155724' : '#856404'
                                    }}>
                                      {task.name || `任务 ${index + 1}`}
                                    </div>
                                    {task.description && (
                                      <div style={{ 
                                        fontSize: '11px', 
                                        color: '#6c757d', 
                                        marginTop: 2 
                                      }}>
                                        {task.description}
                                      </div>
                                    )}
                                    <div style={{ 
                                      fontSize: '10px', 
                                      color: '#6c757d', 
                                      marginTop: 2 
                                    }}>
                                      {task.started_at && `开始：${formatDateTime(task.started_at)}`}
                                      {task.completed_at && ` | 完成：${formatDateTime(task.completed_at)}`}
                                    </div>
                                  </div>
                                  <div style={{ marginLeft: 8 }}>
                                    <Tag 
                                      color={task.completed_at ? 'success' : 'warning'} 
                                      size="small"
                                    >
                                      {task.completed_at ? '已完成' : '进行中'}
                                    </Tag>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch (error) {
                      console.warn('解析入职任务失败:', error);
                    }
                    return null;
                  })()}
                </Timeline.Item>
              )}

              {/* 员工入职 */}
              <Timeline.Item color="green">
                <div><strong>正式入职</strong> - {formatDateTime(employee.hire_date)}</div>
                <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
                  <Space size="middle" wrap>
                    <span>
                      📊 状态：
                      <Tag color={getStatusColor(employee.status)} size="small" style={{ marginLeft: 4 }}>
                        {getStatusText(employee.status)}
                      </Tag>
                    </span>
                    <span>👤 招聘人事：{employee.recruiter_name || '-'}</span>
                  </Space>
                </div>
              </Timeline.Item>

              {/* 转正（如果有） */}
              {employee.regular_date && (
                <Timeline.Item color="cyan">
                  <div><strong>转正</strong> - {formatDateTime(employee.regular_date)}</div>
                  <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
                    🎉 从试用期转为正式员工
                  </div>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>

          {/* 跟进记录历史 */}
          {lifecycleData.followUps && lifecycleData.followUps.length > 0 && (
            <Card title="📝 跟进记录历史" size="small" style={{ marginTop: 16 }}>
              <Timeline size="small">
                {lifecycleData.followUps.map((followUp, index) => (
                  <Timeline.Item key={followUp.id}>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {followUp.created_at} - {followUp.created_by_name || '系统'}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {followUp.content}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          )}

          {/* 相关附件 */}
          {lifecycleData.attachments && lifecycleData.attachments.length > 0 && (
            <Card title="📎 所有相关附件" size="small" style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Alert
                  message="完整附件历史"
                  description="这里展示了该员工从线索到入职后的所有相关附件，包括面试附件、员工附件和入职流程任务附件。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              </div>
              
              {/* 按类型分组显示 */}
              {(() => {
                const groupedAttachments = lifecycleData.attachments.reduce((groups, attachment) => {
                  const type = attachment.attachment_type || 'other';
                  if (!groups[type]) groups[type] = [];
                  groups[type].push(attachment);
                  return groups;
                }, {});

                const typeLabels = {
                  'interview': '面试附件',
                  'employee': '员工附件', 
                  'onboarding_task': '入职任务附件',
                  'other': '其他附件'
                };

                const typeColors = {
                  'interview': '#f0f8ff',
                  'employee': '#f6ffed',
                  'onboarding_task': '#fff7e6',
                  'other': '#f5f5f5'
                };

                return Object.keys(groupedAttachments).map(type => (
                  <div key={type} style={{ marginBottom: 20 }}>
                    <h4 style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '14px', 
                      color: '#333',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '8px'
                    }}>
                      {typeLabels[type]} ({groupedAttachments[type].length}个)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                      {groupedAttachments[type].map((attachment) => {
                        const downloadUrl = attachment.url || `/api/upload/download/${attachment.id}`;
                        const isImage = isImageAttachment(attachment);
                        const displayName = decodeFileName(attachment.original_name || attachment.filename || '附件');

                        // 构建tooltip内容
                        const tooltipContent = (
                          <div style={{ maxWidth: 300 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              {displayName}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: 2 }}>
                              <strong>来源：</strong>{typeLabels[type]}
                            </div>
                            {attachment.task_name && (
                              <div style={{ fontSize: '12px', marginBottom: 2 }}>
                                <strong>任务：</strong>{attachment.task_name}
                              </div>
                            )}
                            <div style={{ fontSize: '12px', marginBottom: 2 }}>
                              <strong>类型：</strong>{isImage ? '图片文件' : '文档文件'}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: 2 }}>
                              <strong>时间：</strong>{attachment.created_at ? new Date(attachment.created_at).toLocaleString() : '未知时间'}
                            </div>
                            {attachment.size && attachment.size > 0 && (
                              <div style={{ fontSize: '12px' }}>
                                <strong>大小：</strong>{(attachment.size / 1024).toFixed(1)} KB
                              </div>
                            )}
                          </div>
                        );

                        return (
                          <Tooltip key={attachment.id} title={tooltipContent} placement="top">
                            <div style={{
                              border: '1px solid #eee',
                              borderRadius: 6,
                              padding: 12,
                              background: typeColors[type] || '#f5f5f5',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#1890ff';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#eee';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            onClick={() => {
                              // 图片一律点击预览（预览器内再提供下载），其他文件直接下载
                              if (isImage) {
                                openImageViewer(downloadUrl, displayName, downloadUrl);
                              } else {
                                // 创建下载链接
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = displayName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                                {typeLabels[type]}
                                {isImage && <span style={{ marginLeft: 4, color: '#1890ff' }}>📷</span>}
                                {attachment.task_name && (
                                  <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                                    任务: {attachment.task_name}
                                  </div>
                                )}
                              </div>
                              <div style={{
                                fontWeight: 'bold',
                                marginBottom: 4,
                                color: '#1890ff',
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {displayName}
                              </div>
                              <div style={{ fontSize: '11px', color: '#999' }}>
                                {attachment.created_at ? new Date(attachment.created_at).toLocaleString() : '未知时间'}
                              </div>
                              <div style={{
                                fontSize: '11px',
                                color: '#666',
                                marginTop: 4,
                                textAlign: 'center',
                                padding: '2px 6px',
                                background: 'rgba(24, 144, 255, 0.1)',
                                borderRadius: '3px'
                              }}>
                                {isImage ? '点击预览' : '点击下载'}
                              </div>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </Card>
          )}
        </div>
      ) : (
        <Empty description="暂无生命周期数据" />
      )}
    </div>
  );

  // 合同Tab内容
  const renderContractTab = () => {
    const getContractStatusColor = (status) => {
      const colorMap = {
        'draft': 'default',
        'active': 'green',
        'expired': 'red',
        'terminated': 'red',
        'renewed': 'blue',
        'expiring_soon': 'orange'
      };
      return colorMap[status] || 'default';
    };

    const getContractStatusText = (status) => {
      const textMap = {
        'draft': '草稿',
        'active': '生效中',
        'expired': '已过期',
        'terminated': '已终止',
        'renewed': '已续签',
        'expiring_soon': '即将到期'
      };
      return textMap[status] || status;
    };

    const getContractTypeText = (type) => {
      const typeMap = {
        'labor': '劳动合同',
        'internship': '实习合同',
        'consultant': '顾问合同',
        'part_time': '兼职合同',
        'other': '其他'
      };
      return typeMap[type] || type;
    };

    const formatDuration = (days) => {
      if (!days) return '-';
      const years = Math.floor(days / 365);
      const months = Math.floor((days % 365) / 30);
      const remainingDays = days % 30;
      
      let result = '';
      if (years > 0) result += `${years}年`;
      if (months > 0) result += `${months}个月`;
      if (remainingDays > 0) result += `${remainingDays}天`;
      
      return result || '0天';
    };

    const getContractCardStyle = (contract) => {
      return { marginBottom: 16 };
    };

    const getContractTitleColor = (contract) => {
      // 如果合同已过期，使用默认颜色
      if (contract.expiry_status === 'expired') {
        return '#000000';
      }
      
      // 如果合同即将到期，根据剩余天数设置标题颜色
      if (contract.expiry_status === 'expiring_soon' && contract.days_until_expiry > 0) {
        const daysLeft = contract.days_until_expiry;
        
        if (daysLeft <= 10) {
          // 10天以内：红色标题
          return '#ff4d4f';
        } else if (daysLeft <= 15) {
          // 15天以内：黄色标题
          return '#faad14';
        } else if (daysLeft <= 30) {
          // 30天以内：橙色标题
          return '#fa8c16';
        }
      }
      
      // 正常状态使用默认颜色
      return '#000000';
    };

    const handleAddContract = () => {
      setEditingContract(null);
      contractForm.resetFields();
      setContractModalVisible(true);
    };

    const handleEditContract = (contract) => {
      setEditingContract(contract);
      contractForm.setFieldsValue({
        ...contract,
        start_date: contract.start_date ? dayjs(contract.start_date) : null,
        end_date: contract.end_date ? dayjs(contract.end_date) : null,
        probation_end_date: contract.probation_end_date ? dayjs(contract.probation_end_date) : null
      });
      setContractModalVisible(true);
    };

    const handleDeleteContract = async (contractId) => {
      // 检查用户权限，只有管理员可以删除合同
      if (!user || (user.role !== 'super_admin' && user.role !== 'hr_manager')) {
        message.error('您没有权限删除合同，请联系管理员');
        return;
      }

      Modal.confirm({
        title: '确认删除',
        content: '确定要删除这个合同吗？删除后无法恢复。',
        onOk: async () => {
          try {
            await api.delete(`/contracts/contracts/${contractId}`);
            message.success('合同删除成功');
            loadEmployeeContracts();
          } catch (error) {
            message.error('删除合同失败');
          }
        }
      });
    };

    const handleContractSubmit = async (values) => {
      try {
        const data = {
          ...values,
          start_date: values.start_date?.format('YYYY-MM-DD'),
          end_date: values.end_date?.format('YYYY-MM-DD'),
          probation_end_date: values.probation_end_date?.format('YYYY-MM-DD')
        };

        if (editingContract) {
          await api.put(`/contracts/contracts/${editingContract.id}`, data);
          message.success('合同更新成功');
        } else {
          await api.post(`/contracts/employees/${employee.id}/contracts`, data);
          message.success('合同创建成功');
        }
        
        setContractModalVisible(false);
        loadEmployeeContracts();
      } catch (error) {
        console.error('合同操作失败:', error);
        
        // 显示具体的错误信息
        let errorMessage = editingContract ? '更新合同失败' : '创建合同失败';
        
        if (error.response && error.response.data) {
          const serverMessage = error.response.data.message;
          if (serverMessage) {
            errorMessage = serverMessage;
          }
        }
        
        message.error(errorMessage);
      }
    };

    return (
      <div>
        <Card 
          title="合同管理" 
          size="small"
          extra={
            <Button type="primary" icon={<FileTextOutlined />} onClick={handleAddContract}>
              新建合同
            </Button>
          }
        >
          {contractsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
          ) : contracts.length === 0 ? (
            <Empty 
              description="暂无合同记录" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={handleAddContract}>
                创建第一个合同
              </Button>
            </Empty>
          ) : (
            <div>
              {contracts.map((contract) => (
                <Card 
                  key={contract.id} 
                  size="small" 
                  style={getContractCardStyle(contract)}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: getContractTitleColor(contract),
                          fontSize: '16px'
                        }}>
                          {contract.contract_name}
                        </span>
                        <Tag 
                          color={getContractStatusColor(contract.expiry_status)} 
                          style={{ marginLeft: 8 }}
                        >
                          {getContractStatusText(contract.expiry_status)}
                        </Tag>
                        {contract.expiry_status === 'expiring_soon' && contract.days_until_expiry > 0 && (
                          <Tag color="orange" style={{ marginLeft: 8 }}>
                            还有{contract.days_until_expiry}天到期
                          </Tag>
                        )}
                        {contract.expiry_status === 'expired' && (
                          <Tag color="red" style={{ marginLeft: 8 }}>
                            已过期{Math.abs(contract.days_until_expiry)}天
                          </Tag>
                        )}
                      </div>
                      <Space>
                        <Button size="small" icon={<UploadOutlined />} onClick={() => handleUploadAttachment(contract.id)}>
                          上传扫描件
                        </Button>
                        <Button size="small" onClick={() => handleEditContract(contract)}>
                          编辑
                        </Button>
                        {(user?.role === 'super_admin' || user?.role === 'hr_manager') && (
                          <Button size="small" danger onClick={() => handleDeleteContract(contract.id)}>
                            删除
                          </Button>
                        )}
                      </Space>
                    </div>
                  }
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <div><strong>合同类型：</strong>{getContractTypeText(contract.contract_type)}</div>
                      <div><strong>合同编号：</strong>{contract.contract_number || '-'}</div>
                      <div><strong>合同期限：</strong>{formatDuration(contract.duration_days)}</div>
                    </Col>
                    <Col span={8}>
                      <div><strong>开始日期：</strong>{contract.start_date}</div>
                      <div><strong>结束日期：</strong>{contract.end_date}</div>
                      <div><strong>剩余天数：</strong>
                        <span style={{ 
                          color: contract.days_until_expiry < 30 ? '#ff4d4f' : '#52c41a' 
                        }}>
                          {contract.days_until_expiry}天
                        </span>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div><strong>工作地点：</strong>{contract.work_location || '-'}</div>
                      <div><strong>试用期：</strong>
                        {contract.probation_period_days ? `${contract.probation_period_days}天` : '-'}
                      </div>
                      <div><strong>创建人：</strong>{contract.created_by_name || '未知'}</div>
                      <div><strong>创建时间：</strong>{contract.created_at ? new Date(contract.created_at).toLocaleString('zh-CN') : '-'}</div>
                    </Col>
                  </Row>
                  
                  {contract.notes && (
                    <div style={{ marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                      <strong>备注：</strong>{contract.notes}
                    </div>
                  )}

                  {contract.attachments && contract.attachments.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <strong>扫描件：</strong>
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {contract.attachments.map((attachment) => {
                          const isImage = attachment.file_type && attachment.file_type.startsWith('image/');
                          const downloadUrl = `/api/upload/download/${attachment.id}`;
                          
                          return (
                            <Tooltip
                              key={attachment.id}
                              title={
                                <div>
                                  <div>文件名：{attachment.file_name}</div>
                                  <div>上传时间：{attachment.created_at}</div>
                                  <div>上传人：{attachment.upload_by_name || '未知'}</div>
                                  <div>文件大小：{(attachment.file_size / 1024 / 1024).toFixed(2)}MB</div>
                                </div>
                              }
                              placement="top"
                            >
                              <div
                                style={{
                                  position: 'relative',
                                  width: '120px',
                                  height: '120px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  background: '#fafafa',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#1890ff';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#d9d9d9';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                onClick={() => {
                                  if (isImage) {
                                    setCurrentImage({
                                      url: downloadUrl,
                                      name: attachment.file_name,
                                      downloadUrl: downloadUrl
                                    });
                                    setImageViewerVisible(true);
                                  } else {
                                    // 非图片文件直接下载
                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.download = attachment.file_name;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                              >
                                {isImage ? (
                                  <img
                                    src={downloadUrl}
                                    alt={attachment.file_name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      // 图片加载失败时显示文件图标
                                      e.target.style.display = 'none';
                                      const fileName = attachment.file_name || '未知文件';
                                      e.target.parentElement.innerHTML = `
                                        <div style="text-align: center; padding: 20px;">
                                          <div style="font-size: 24px; color: #1890ff; margin-bottom: 8px;">📄</div>
                                          <div style="font-size: 12px; color: #666; word-break: break-all;">${fileName}</div>
                                        </div>
                                      `;
                                    }}
                                  />
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <div style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }}>
                                      {attachment.file_type?.includes('pdf') ? '📄' : 
                                       attachment.file_type?.includes('word') ? '📝' : '📎'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
                                      {attachment.file_name}
                                    </div>
                                  </div>
                                )}
                                
                                {/* 删除按钮 */}
                                {(user?.role === 'super_admin' || user?.role === 'hr_manager') && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      background: 'rgba(0, 0, 0, 0.6)',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      zIndex: 1
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAttachment(contract.id, attachment.id);
                                    }}
                                  >
                                    ×
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* 合同编辑弹窗 */}
        <Modal
          title={editingContract ? '编辑合同' : '新建合同'}
          open={contractModalVisible}
          onCancel={() => setContractModalVisible(false)}
          onOk={() => contractForm.submit()}
          width={800}
        >
          <Form
            form={contractForm}
            layout="vertical"
            onFinish={handleContractSubmit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="contract_type"
                  label="合同类型"
                  rules={[{ required: true, message: '请选择合同类型' }]}
                >
                  <Select placeholder="请选择合同类型">
                    <Option value="labor">劳动合同</Option>
                    <Option value="internship">实习合同</Option>
                    <Option value="consultant">顾问合同</Option>
                    <Option value="part_time">兼职合同</Option>
                    <Option value="other">其他</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contract_name"
                  label="合同名称"
                  rules={[{ required: true, message: '请输入合同名称' }]}
                >
                  <Input placeholder="请输入合同名称" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contract_number" label="合同编号">
                  <Input placeholder="请输入合同编号" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="work_location" label="工作地点">
                  <Select
                    placeholder="请选择工作地点"
                    allowClear
                    options={[
                      { label: '洛阳', value: '洛阳' },
                      { label: '汝州', value: '汝州' }
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="start_date"
                  label="开始日期"
                  rules={[{ required: true, message: '请选择开始日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="end_date"
                  label="结束日期"
                  rules={[{ required: true, message: '请选择结束日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="probation_period_days" label="试用期天数">
                  <InputNumber style={{ width: '100%' }} placeholder="试用期天数" min={0} />
                </Form.Item>
              </Col>
            </Row>


            <Form.Item name="notes" label="备注">
              <Input.TextArea rows={3} placeholder="请输入备注信息" />
            </Form.Item>
          </Form>
        </Modal>

        {/* 合同扫描件上传弹窗 */}
        <Modal
          title="上传合同扫描件"
          open={contractUploadModalVisible}
          onCancel={() => {
            setContractUploadModalVisible(false);
            setUploadingFiles([]);
          }}
          onOk={handleUploadSubmit}
          confirmLoading={uploading}
          width={600}
        >
          <Upload.Dragger
            multiple
            fileList={uploadingFiles}
            onChange={handleFileChange}
            beforeUpload={() => false} // 阻止自动上传
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            showUploadList={{
              showPreviewIcon: false,
              showRemoveIcon: true,
              showDownloadIcon: false
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持单个或批量上传，支持格式：PDF、JPG、PNG、DOC、DOCX
              <br />
              单个文件不超过10MB
            </p>
          </Upload.Dragger>
        </Modal>
      </div>
    );
  };


  return (
    <div className="employee-detail">
      <style>{EMPLOYEE_DETAIL_CSS}</style>
      {/* 头部信息 */}
      {(() => {
        const deptName = employee.department_name
          || departments.find(d => d.id === employee.department_id)?.name
          || '';
        const posName = employee.position_name
          || positions.find(p => p.id === employee.position_id)?.name
          || '';
        const metaItems = [
          employee.employee_no ? `工号 ${employee.employee_no}` : '',
          deptName,
          posName,
          employee.hire_date ? `入职 ${dayjs(employee.hire_date).format('YYYY-MM-DD')}` : ''
        ].filter(Boolean);
        return (
      <div className="emp-header">
        <div className="emp-header-main">
          <div className="emp-avatar">
            {(employee.name || '?').charAt(0)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="emp-header-title">
              <span className="emp-header-name">{employee.name || '-'}</span>
              <Tag color={getStatusColor(employee.status)} style={{ marginInlineStart: 0 }}>
                {getStatusText(employee.status)}
              </Tag>
            </div>
            {metaItems.length > 0 && (
              <div className="emp-header-meta">
                {metaItems.map((item, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="dot">·</span>}
                    <span>{item}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="emp-header-actions">
          <Space wrap>
            {/* 试用期员工可以转正 */}
            {employee.status === 'probation' && (
              <Button
                type="primary"
                icon={<UserOutlined />}
                onClick={() => { setRegularModalVisible(true); setRegularDate(dayjs()); }}
              >
                转正
              </Button>
            )}
            {/* 试用期和正式员工都可以离职 */}
            {(employee.status === 'probation' || employee.status === 'regular') && (
              <Button
                danger
                icon={<UserDeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: '确认发起离职',
                    content: '确定要为该员工发起离职流程吗？',
                    okText: '确认',
                    cancelText: '取消',
                    onOk: () => {
                      setResignModalVisible(true);
                      setResignDate(dayjs().add(1, 'month'));
                    }
                  });
                }}
              >
                办理离职
              </Button>
            )}
          </Space>
        </div>
      </div>
        );
      })()}

      {/* 主要内容区域 - 使用Tabs分割 */}
      <Tabs
        defaultActiveKey="basic"
        items={[
          {
            key: 'basic',
            label: (
              <span>
                <UserOutlined />
                基本信息
              </span>
            ),
            children: renderBasicInfoTab()
          },
          {
            key: 'attachments',
            label: (
              <span>
                <PaperClipOutlined />
                附件
              </span>
            ),
            children: renderAttachmentsTab()
          },
          {
            key: 'tasks',
            label: (
              <span>
                <CheckSquareOutlined />
                任务
              </span>
            ),
            children: renderTasksTab()
          },
          {
            key: 'salary',
            label: (
              <span>
                <DollarOutlined />
                薪资管理
              </span>
            ),
            children: renderSalaryTab()
          },
          {
            key: 'lifecycle',
            label: (
              <span>
                <CalendarOutlined />
                生命周期
              </span>
            ),
            children: renderLifecycleTab()
          },
          {
            key: 'contract',
            label: (
              <span>
                <FileTextOutlined />
                合同
              </span>
            ),
            children: renderContractTab()
          }
        ]}
        size="large"
        style={{ marginTop: 16 }}
      />

      {/* 转正弹窗 */}
      <Modal
        title="转正"
        open={regularModalVisible}
        onCancel={() => setRegularModalVisible(false)}
        onOk={async () => {
          try {
            const dateStr = regularDate ? regularDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
            // 通过父级保存，便于刷新列表
            await onSave(employee.id, { status: 'regular', regular_date: dateStr });
            setRegularModalVisible(false);
          } catch (e) {
            message.error('转正失败');
          }
        }}
      >
        <div style={{ marginBottom: 8 }}>转正日期</div>
        <DatePicker style={{ width: '100%' }} value={regularDate} onChange={setRegularDate} />
      </Modal>

      {/* 发起离职弹窗 */}
      <Modal
        title="发起离职"
        open={resignModalVisible}
        width={800}
        onCancel={() => {
          setResignModalVisible(false);
          setResignReason('');
          setResignAttachments([]);
        }}
        onOk={async () => {
          try {
            const lastDay = resignDate ? resignDate.format('YYYY-MM-DD') : null;
            // 创建离职工单
            await api.post('/resignations', { 
              employee_id: employee.id, 
              reason: resignReason, 
              last_working_day: lastDay,
              application_attachments: resignAttachments
            });
            // 更新状态为离职中
            await onSave(employee.id, { status: 'resigning' });
            message.success('已发起离职工单');
            setResignModalVisible(false);
            setResignReason('');
            setResignAttachments([]);
          } catch (e) {
            message.error('发起离职失败');
          }
        }}
      >
        <div style={{ display: 'flex', gap: '24px', minHeight: '400px' }}>
          {/* 左侧：表单信息 */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>最后工作日</div>
              <DatePicker style={{ width: '100%' }} value={resignDate} onChange={setResignDate} />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>离职原因</div>
              <Input.TextArea 
                rows={6} 
                value={resignReason} 
                onChange={e => setResignReason(e.target.value)} 
                placeholder="请输入离职原因（可选）" 
              />
            </div>
          </div>
          
          {/* 右侧：上传组件 */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>上传离职申请单</div>
            <AttachmentUpload
              value={resignAttachments}
              onChange={setResignAttachments}
              compact={false}
              showQrButton={true}
              attachToLead={false}
            />
          </div>
        </div>
      </Modal>

      {/* 上传附件弹窗 */}
      <Modal
        title="上传附件"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <AttachmentUpload 
          employeeId={employee.id} 
          compact={false}
          showQrButton={false}
          hideList={false}
          attachToLead={false}
          onChange={loadEmployeeAttachments}
        />
      </Modal>

      {/* 薪资设置表单 */}
      <EmployeeSalaryForm
        visible={salaryModalVisible}
        onCancel={() => setSalaryModalVisible(false)}
        onSubmit={handleSalarySubmit}
        employee={employee}
        templates={salaryTemplates}
        salaryItems={[]} // 不需要预设项目，支持自定义
        initialData={salaryData}
      />

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />

      {/* 业绩薪资计算模态框 */}
      <Modal
        title="业绩薪资计算"
        open={performanceModalVisible}
        onCancel={() => setPerformanceModalVisible(false)}
        footer={null}
        width={850}
      >
        <PerformanceSalaryCalculator
          visible={performanceModalVisible}
          onCancel={() => setPerformanceModalVisible(false)}
          onCalculate={(result) => {
            console.log('业绩薪资计算结果:', result);
            message.success('业绩薪资计算完成');
            // 这里可以添加将计算结果应用到员工薪资的逻辑
            setPerformanceModalVisible(false);
            // 重新加载薪资数据
            loadEmployeeSalary();
          }}
          initialData={{
            department_id: employee.department_id,
            employee_id: employee.id,
            period: new Date().toISOString().split('T')[0].substring(0, 7) // 默认当前月份
          }}
        />
      </Modal>
    </div>
  );
};

export default EmployeeDetail;
